import { BaseMarkerRenderer } from '../base/baseMarkerRenderer'
import { BezierLineMarker } from './bezierLineMarker'
import { Point, AnchorPoints, ControlPoints, MarkerModes } from '../../types'

interface DrawBezierOptions {
  isDashed: boolean
  dashLength: number
  dashSpaceLength: number
}

function drawBezier(gfx, anchorPoints: AnchorPoints, controlPoints: ControlPoints, options?: DrawBezierOptions) {
  if (options?.isDashed === true) {
    drawDashedBezierCurve(gfx, anchorPoints, controlPoints, options.dashLength, options.dashSpaceLength)
  } else drawSolidBezier(gfx, anchorPoints, controlPoints)
}

function drawSolidBezier(gfx, anchorPoints: AnchorPoints, controlPoints: ControlPoints) {
  gfx.beginShape()
  anchorPoints.forEach((anchor, i) => {
    //p5 requires first point be a regular vertex
    if (i === 0) {
      gfx.vertex(anchor.x, anchor.y)
    } else {
      const startControlPoint = controlPoints[i - 1].start
      //Modulo ensures wrap around to first point
      const endControlPoint = controlPoints[i - 1].end

      gfx.bezierVertex(
        startControlPoint.x,
        startControlPoint.y,
        endControlPoint.x,
        endControlPoint.y,
        anchor.x,
        anchor.y
      )
    }
  })
  gfx.endShape()
}

function drawDashedBezierCurve(
  gfx,
  anchorPoints: AnchorPoints,
  controlPoints: ControlPoints,
  dashLength: number,
  spaceLength: number
) {
  for (let i = 0; i < anchorPoints.length - 1; i++) {
    drawDashedBezierLine(
      gfx,
      anchorPoints[i],
      controlPoints[i].start,
      controlPoints[i].end,
      anchorPoints[i + 1],
      dashLength,
      spaceLength
    )
  }
}

function drawDashedBezierLine(
  gfx,
  a1: Point,
  c1: Point,
  c2: Point,
  a2: Point,
  dashLength: number,
  spaceLength: number
) {
  const bezierPoint = gfx.bezierPoint
  const dist = gfx.dist
  const lerp = gfx.lerp

  const detail = 1000 // Higher detail for smoother curve
  const points = []

  // Calculate points along the Bezier curve
  for (let i = 0; i <= detail; i++) {
    const t = i / detail
    const x = bezierPoint(a1.x, c1.x, c2.x, a2.x, t)
    const y = bezierPoint(a1.y, c1.y, c2.y, a2.y, t)
    points.push({ x, y })
  }

  let dash = true // State to track whether to draw dash or space
  let accumulatedLength = 0
  let currentIndex = 0

  while (currentIndex < points.length - 1) {
    const start = points[currentIndex]
    const end = points[currentIndex + 1]
    const segmentLength = dist(start.x, start.y, end.x, end.y)
    accumulatedLength += segmentLength

    if (dash) {
      if (accumulatedLength < dashLength) {
        // Continue the line segment
        gfx.line(start.x, start.y, end.x, end.y)
      } else {
        // Finish the current dash
        const overshoot = accumulatedLength - dashLength
        const backtrack = overshoot / segmentLength
        const finalX = lerp(end.x, start.x, backtrack)
        const finalY = lerp(end.y, start.y, backtrack)
        gfx.line(start.x, start.y, finalX, finalY)
        dash = false
        accumulatedLength = 0
      }
    } else {
      if (accumulatedLength >= spaceLength) {
        // Start a new dash
        dash = true
        accumulatedLength = 0
      }
    }
    currentIndex++
  }
}

class BezierLineMarkerRenderer extends BaseMarkerRenderer<BezierLineMarker> {
  declare marker: BezierLineMarker

  constructor(marker: BezierLineMarker) {
    super(marker)
  }

  drawDashedBezierCurve(
    gfx,
    anchorPoints: AnchorPoints,
    controlPoints: ControlPoints,
    dashLength: number,
    spaceLength: number
  ) {
    for (let i = 0; i < anchorPoints.length - 1; i++) {
      this.drawDashedBezier(
        gfx,
        anchorPoints[i],
        controlPoints[i].start,
        controlPoints[i].end,
        anchorPoints[i + 1],
        dashLength,
        spaceLength
      )
    }
  }

  drawDashedBezier(gfx, a1: Point, c1: Point, c2: Point, a2: Point, dashLength: number, spaceLength: number) {
    const bezierPoint = gfx.bezierPoint
    const dist = gfx.dist
    const lerp = gfx.lerp

    const detail = 1000 // Higher detail for smoother curve
    const points = []

    // Calculate points along the Bezier curve
    for (let i = 0; i <= detail; i++) {
      const t = i / detail
      const x = bezierPoint(a1.x, c1.x, c2.x, a2.x, t)
      const y = bezierPoint(a1.y, c1.y, c2.y, a2.y, t)
      points.push({ x, y })
    }

    let dash = true // State to track whether to draw dash or space
    let accumulatedLength = 0
    let currentIndex = 0

    while (currentIndex < points.length - 1) {
      const start = points[currentIndex]
      const end = points[currentIndex + 1]
      const segmentLength = dist(start.x, start.y, end.x, end.y)
      accumulatedLength += segmentLength

      if (dash) {
        if (accumulatedLength < dashLength) {
          // Continue the line segment
          gfx.line(start.x, start.y, end.x, end.y)
        } else {
          // Finish the current dash
          const overshoot = accumulatedLength - dashLength
          const backtrack = overshoot / segmentLength
          const finalX = lerp(end.x, start.x, backtrack)
          const finalY = lerp(end.y, start.y, backtrack)
          gfx.line(start.x, start.y, finalX, finalY)
          dash = false
          accumulatedLength = 0
        }
      } else {
        if (accumulatedLength >= spaceLength) {
          // Start a new dash
          dash = true
          accumulatedLength = 0
        }
      }
      currentIndex++
    }
  }

  //TODO add typing fo gfx
  drawBezier(gfx, anchorPoints: AnchorPoints, controlPoints: ControlPoints) {
    gfx.beginShape()
    anchorPoints.forEach((anchor, i) => {
      //p5 requires first point be a regular vertex
      if (i === 0) {
        gfx.vertex(anchor.x, anchor.y)
      } else {
        const startControlPoint = controlPoints[i - 1].start
        //Modulo ensures wrap around to first point
        const endControlPoint = controlPoints[i - 1].end

        gfx.bezierVertex(
          startControlPoint.x,
          startControlPoint.y,
          endControlPoint.x,
          endControlPoint.y,
          anchor.x,
          anchor.y
        )
      }
    })
    gfx.endShape()
  }

  renderHandles() {
    const state = this.marker.state
    const mode = state.mode

    if (mode === MarkerModes.Edit || mode === MarkerModes.Create) {
      state.handles.forEach((handle) => {
        handle.render()
      })
    }
  }

  renderGuideLine() {
    const marker = this.marker
    const state = this.marker.state
    const controller = this.marker.controller
    const p = marker.p
    const shouldDraw =
      state.mode === MarkerModes.Create &&
      state.anchorPoints.length > 0 &&
      !state.pressedHandle &&
      !p.keyIsDown(p.CONTROL)
    const lastAnchorPoint = state.anchorPoints[state.anchorPoints.length - 1]

    if (shouldDraw) {
      p.beginShape()
      marker.p.vertex(lastAnchorPoint.x, lastAnchorPoint.y)
      marker.p.vertex(controller.mouseCoordinateX, controller.mouseCoordinateY)
      p.endShape()
    }
  }

  renderShape() {
    const marker = this.marker
    const state = marker.state
    const shouldDraw = state.anchorPoints.length > 1 && state.controlPoints.length === state.anchorPoints.length - 1
    const isDashed = state.isDashed

    if (shouldDraw) {
      const dashLength = state.dashLength
      const dashSpaceLength = state.dashSpaceLength
      drawBezier(this.marker.p, state.anchorPoints, state.controlPoints, {
        isDashed: isDashed,
        dashLength: dashLength,
        dashSpaceLength: dashSpaceLength,
      })
    }
  }

  renderMarker(): void {
    this.renderShape()
    this.renderHandles()
    this.renderGuideLine()
  }
}

export { BezierLineMarkerRenderer }
